import {DialogResult, DialogTypes} from 'common/names';
import {pageSelectors} from 'features/layout/CommonPage/redux';
import {ICloseSubDialogAction} from 'features/layout/redux/actions';
import {call, select, SagaReturnType} from 'redux-saga/effects';
import i18n from 'i18n';
import {asyncSagaConstructor, unhandedSagaError} from 'redux/common';
import api from 'api';
import {backupTypes} from 'redux/backuprestore';

import qs from 'query-string';
import ApiService from 'api/ApiService';
import {CancelableConfirmationDialogResult} from 'components/dialogs/CancelableConfirmationDialog';
import {openDialogWithResult} from 'redux/common/saga/openDialogWithResult';
import {openMergeDialog} from './openMergeDialog';
import {PendingChangesDialogData} from 'features/layout/DialogRoutes/dialogs/PendingChangesDialog';
import {getBackupFilePath} from '../getBackupFilePath';
import {getPassword} from 'redux/common/saga/getPassword';

export function* mergeSaga() {
   try {
      //if there are changes pending, find out if they want to continue
      //and lose the changes or quit
      const isPendingChanges = (yield select(pageSelectors.isPendingChanges)) as ReturnType<
         typeof pageSelectors.isPendingChanges
      >;
      if (isPendingChanges) {
         const result: ICloseSubDialogAction<boolean> = yield call(openDialogWithResult, {
            id: DialogTypes.PendingChangesDialog.id,
            data: {
               processLabel: i18n.t('merge'),
               confirmLabel: i18n.t('Merge'),
            } as PendingChangesDialogData,
         });
         if (result.payload?.data) {
            //lose changes
            //   yield put(action(pageTypes.RESET_ALL_FORMS));
         } else {
            //quit
            return;
         }
      }

      const fileManagerResult = (yield call(
         getBackupFilePath,
         i18n.t('Merge Transaction Datastore From'),
         true
      )) as SagaReturnType<typeof getBackupFilePath>;

      const {filePath: dbFilePath, suspend = true} = fileManagerResult || {};
      if (!dbFilePath) {
         return;
      }

      const {result: isEncrypted} = yield call(
         asyncSagaConstructor(
            qs.stringifyUrl({url: api.isEncrypted, query: {filePath: dbFilePath}}),
            backupTypes.IS_ENCRYPTED
         )
      );

      let password = '';
      if (isEncrypted) {
         password =
            ((yield call(
               getPassword,
               i18n.t('Confirm Merge File Password'),
               i18n.t('Enter Merge File Password')
            )) as SagaReturnType<typeof getPassword>) || '';
         if (!password) {
            return;
         }
      }

      const {result: overwritePossible} = yield call(
         asyncSagaConstructor(api.parseMerge, backupTypes.PARSE_MERGE, {
            getMethod: ApiService.postSecureJson,
            data: {
               filePath: dbFilePath,
               password,
            },
         })
      );

      let shouldOverwrite = CancelableConfirmationDialogResult.NO;
      if (overwritePossible) {
         shouldOverwrite = (yield call(isOverwriteConfirmed)) as CancelableConfirmationDialogResult;
      }

      if (shouldOverwrite !== CancelableConfirmationDialogResult.CANCEL) {
         const overwrite = shouldOverwrite === CancelableConfirmationDialogResult.OK;
         (yield call(openMergeDialog, dbFilePath, password, overwrite, suspend)) as SagaReturnType<
            typeof openMergeDialog
         >;
      }
   } catch (e) {
      (yield call(unhandedSagaError, e as Error)) as SagaReturnType<typeof unhandedSagaError>;
   }
}

function* isOverwriteConfirmed() {
   //find out if they want to overwrite
   const dialogResult: ICloseSubDialogAction<CancelableConfirmationDialogResult> = yield call(
      openDialogWithResult,
      {
         id: DialogTypes.CancelableConfirmationDialog.id,
         data: {
            confirmLabel: i18n.t('Yes'),
            title: i18n.t('Merge Overwrite?'),
            content: i18n.t('Should duplicate items be overwritten during the merge?'),
         },
      }
   );
   let shouldOverwrite = dialogResult.payload?.data;

   if (shouldOverwrite === CancelableConfirmationDialogResult.OK) {
      //give them the warning about overwriting causing all jobs to be
      //suspended and see if they want to continue
      const dialogResult: ICloseSubDialogAction<null> = yield call(openDialogWithResult, {
         id: DialogTypes.ConfirmDialog.id,
         data: {
            confirmLabel: i18n.t('Yes'),
            cancelLabel: i18n.t('No'),
            title: i18n.t('Transaction Datastore Overwrite During Merge'),
            content: i18n.t(
               'Duplicate items found and overwrite was selected.\n\n' +
                  'Before the Transaction Datastore can be merged all job scheduling\n' +
                  'will be suspended and processing will wait for all currently executing\n' +
                  'jobs to finish running.\n\n' +
                  'Do you want to continue?'
            ),
         },
      });

      if (!dialogResult.payload?.data) {
         shouldOverwrite = CancelableConfirmationDialogResult.CANCEL;
      }
   }

   return shouldOverwrite;
}
